import { c as _c } from "react/compiler-runtime";
import { placementEngine } from "@theconversation/promos-client";
import { THE_CONVERSATION_VENDOR_ID, THE_CONVERSATION_PROMOS_PURPOSE } from "@theconversation/shared-libraries/Didomi";
import { createContext, PropsWithChildren, useEffect, useState } from "react";
import { usePromosData } from "modules/promos/hooks/usePromosData";
import throttle from "lodash/throttle";
import type { PromoDataProps } from "modules/promos/types";
import { EffectFn1 } from "common/lib/types";
import { useConsent } from "common/hooks/data/useConsent";
import { getLocalStorage } from "common/lib/getLocalStorage";
import { useRegionCode } from "modules/i18n/hooks/useRegionCode";
import { useViewer } from "common/hooks/data/useViewer";
import { useViewerSubscriptions } from "common/hooks/data/useViewerSubscriptions";

// A `PlacementEngineEvent` defines events coming from the placement engine. These events require the promo as
// an argument, so the placement engine can update the user local storage based on user interaction (engagement, block, etc.)
export type PlacementEngineEvent = EffectFn1<PromoDataProps>;

// The placement engine observable is the resulting object after it processes the promos pipeline. It is accessible
// by subscribing the engine's signal.
type PlacementEngineObservable = {
  promos: PromoDataProps[];
  onClose: PlacementEngineEvent;
  onClick: PlacementEngineEvent;
  onView: PlacementEngineEvent;
  onRefresh: PlacementEngineEvent;
};
export type PromosPlacementEngineProps = {
  promos: PromoDataProps[];
  events: {
    onClose: PlacementEngineEvent;
    onClick: PlacementEngineEvent;
    onView: PlacementEngineEvent;
  };
};
export const PromosPlacementEngineContext = createContext<PromosPlacementEngineProps>({
  promos: [],
  events: {
    onClose: () => {},
    onClick: () => {},
    onView: () => {}
  }
});
export const PromosContext = t0 => {
  "use memo";

  const $ = _c(23);
  const {
    children
  } = t0;
  const candidatePromos = usePromosData();
  const consent = useConsent(THE_CONVERSATION_VENDOR_ID, THE_CONVERSATION_PROMOS_PURPOSE);
  const region = useRegionCode();
  const {
    viewer
  } = useViewer();
  const {
    getAllSubscribedNewsletters
  } = useViewerSubscriptions();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [promos, setPromos] = useState(t1);
  const [onClose, setOnClose] = useState(_temp2);
  const [onClick, setOnClick] = useState(_temp4);
  const [onView, setOnView] = useState(_temp6);
  const [onRefresh, setOnRefresh] = useState(_temp8);
  let t2;
  let t3;
  if ($[1] !== candidatePromos || $[2] !== consent || $[3] !== getAllSubscribedNewsletters || $[4] !== region || $[5] !== viewer) {
    t2 = () => {
      if (!viewer) {
        return;
      }
      if (candidatePromos.length === 0) {
        return;
      }
      const context = {
        consent,
        region,
        user: {
          donations: viewer.user?.donations,
          newsletters: {
            subscribedIds: getAllSubscribedNewsletters().map(_temp9)
          }
        }
      };
      const signal = placementEngine(getLocalStorage(), candidatePromos, context);
      signal.subscribe(observable => {
        setPromos(observable.promos);
        setOnClose(() => observable.onClose);
        setOnClick(() => observable.onClick);
        setOnView(() => observable.onView);
        setOnRefresh(() => observable.onRefresh);
      });
      return () => {
        signal.unsubscribe();
      };
    };
    t3 = [candidatePromos, consent, region, viewer, getAllSubscribedNewsletters];
    $[1] = candidatePromos;
    $[2] = consent;
    $[3] = getAllSubscribedNewsletters;
    $[4] = region;
    $[5] = viewer;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== onRefresh) {
    t4 = () => {
      const handleRefresh = throttle(() => onRefresh(), 1000);
      document.addEventListener("resize", handleRefresh);
      document.addEventListener("scroll", handleRefresh, {
        passive: true
      });
      return () => {
        document.removeEventListener("scroll", handleRefresh);
        document.removeEventListener("resize", handleRefresh);
      };
    };
    $[8] = onRefresh;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== candidatePromos || $[11] !== onRefresh) {
    t5 = [candidatePromos, onRefresh];
    $[10] = candidatePromos;
    $[11] = onRefresh;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== onClick || $[14] !== onClose || $[15] !== onView) {
    t6 = {
      onClose,
      onClick,
      onView
    };
    $[13] = onClick;
    $[14] = onClose;
    $[15] = onView;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== promos || $[18] !== t6) {
    t7 = {
      promos,
      events: t6
    };
    $[17] = promos;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== children || $[21] !== t7) {
    t8 = <PromosPlacementEngineContext.Provider value={t7}>{children}</PromosPlacementEngineContext.Provider>;
    $[20] = children;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
function _temp() {}
function _temp2() {
  return _temp;
}
function _temp3() {}
function _temp4() {
  return _temp3;
}
function _temp5() {}
function _temp6() {
  return _temp5;
}
function _temp7() {}
function _temp8() {
  return _temp7;
}
function _temp9(t0) {
  const {
    id
  } = t0;
  return id;
}